/**
 * Verse pack option pools + post-collection art overflow.
 * Used by endless free play (and later any pack faucet).
 */
import { CATALOG } from "../deck/catalog";
import {
  GALLERY_CHAPTER_PIECES,
  artIdsForOwnVerse,
} from "./gallery";
import { MAX_COPIES } from "./starter";
import {
  copiesOwned,
  grantCards,
  isArtUnlocked,
  loadCollection,
  unlockArt,
} from "./storage";

/**
 * Verses that must be earned from campaign first (signature / chapter medals).
 * Once owned, packs may offer stacks.
 */
export const PACK_GATED_UNTIL_OWNED: readonly string[] = [
  "neh-8-10",
  "eccl-4-9-10",
];

function isPackEligible(verseId: string): boolean {
  if (!CATALOG.some((v) => v.id === verseId)) return false;
  if (PACK_GATED_UNTIL_OWNED.includes(verseId) && copiesOwned(verseId) < 1) {
    return false;
  }
  return true;
}

function shuffleInPlace<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = t;
  }
  return arr;
}

/** True when every catalog verse is at MAX_COPIES (gated-unowned excluded). */
export function isCollectionMaxed(): boolean {
  for (const v of CATALOG) {
    if (!isPackEligible(v.id)) {
      // Gated and not yet owned — collection not "maxed" for packs
      if (copiesOwned(v.id) < 1) return false;
      continue;
    }
    if (copiesOwned(v.id) < MAX_COPIES) return false;
  }
  return true;
}

/** How many unique verses still need a first copy (pack-eligible). */
export function unownedPackCount(): number {
  return CATALOG.filter(
    (v) => isPackEligible(v.id) && copiesOwned(v.id) === 0,
  ).length;
}

/**
 * Build up to `count` pack options.
 * Priority: unowned → under MAX_COPIES stacks. Never offers maxed-only filler.
 */
export function buildPackOptions(count = 3): string[] {
  const unowned: string[] = [];
  const stacks: string[] = [];
  for (const v of CATALOG) {
    if (!isPackEligible(v.id)) continue;
    const n = copiesOwned(v.id);
    if (n <= 0) unowned.push(v.id);
    else if (n < MAX_COPIES) stacks.push(v.id);
  }
  shuffleInPlace(unowned);
  shuffleInPlace(stacks);
  const out: string[] = [];
  for (const id of unowned) {
    if (out.length >= count) break;
    out.push(id);
  }
  for (const id of stacks) {
    if (out.length >= count) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Chapter gallery pieces still locked (for overflow rewards). */
export function lockedChapterArtIds(): string[] {
  return GALLERY_CHAPTER_PIECES.filter((p) => !isArtUnlocked(p.id)).map(
    (p) => p.id,
  );
}

/**
 * Unlock the next locked chapter art piece(s).
 * Bypasses mission gates — endless overflow / mastery drip only.
 */
export function grantOverflowArt(count = 1): string[] {
  const locked = lockedChapterArtIds();
  if (!locked.length) return [];
  shuffleInPlace(locked);
  return unlockArt(locked.slice(0, Math.max(1, count)));
}

export type PackGrantResult = {
  /** Verse ids that gained a copy. */
  gained: string[];
  /** Gallery art unlocked this grant (verse faces and/or overflow). */
  artUnlocked: string[];
  /** True when the pick was at cap and we converted to art instead. */
  convertedToArt: boolean;
};

/**
 * Apply a pack pick: +1 copy if under cap, else overflow chapter art.
 */
export function applyPackPick(verseId: string): PackGrantResult {
  const before = copiesOwned(verseId);
  if (before >= MAX_COPIES) {
    const art = grantOverflowArt(1);
    // Ensure face art exists even at cap
    const face = unlockArt(artIdsForOwnVerse(verseId));
    return {
      gained: [],
      artUnlocked: [...new Set([...art, ...face])],
      convertedToArt: art.length > 0,
    };
  }
  const colBefore = loadCollection();
  const artBefore = new Set(colBefore.unlockedArt);
  const gained = grantCards([verseId]);
  const colAfter = loadCollection();
  const artUnlocked = colAfter.unlockedArt.filter((id) => !artBefore.has(id));
  return { gained, artUnlocked, convertedToArt: false };
}

/** When no card options remain, try pure art pack. */
export function applyArtPack(count = 1): string[] {
  return grantOverflowArt(count);
}

export function hasAnyPackRewardLeft(): boolean {
  if (buildPackOptions(1).length > 0) return true;
  if (lockedChapterArtIds().length > 0) return true;
  return false;
}
